/**
 * Quick stops (Kurtis 2026-10-24, docs/plans/ADD_NOW_PLAN.md phase 2): a stop
 * with no length, at an exact minute — the fountain on a stroll, looked at for
 * three minutes. Stored as `quick_stop` (migration 320) with no duration: a
 * missing or 0 duration already means "unknown" and reads as an hour almost
 * everywhere, so the marker is its own field and every surface asks this file.
 *
 * - Chosen, never the default: shrinking a stop below QUICK_STOP_UNDER in
 *   Change time makes it one (AddWhenControls), and so does ending a running
 *   "I'm here" stop that short (chaining, below).
 * - It never clashes, in either direction, and draws as a dot, not a block.
 * - Quick stops within QUICK_RUN_GAP of each other read as one row on the day
 *   grid ("Stroll · 5 stops · 3:00–3:25").
 *
 * Chaining: an "I'm here" while the last "I'm here" stop is still running
 * ends that stop at this minute (`addedVia: 'here'`). It is the one time an
 * add touches another stop, and never a planned one.
 *
 * Pure — no React, no i18n, no clock.
 */
import { parseDurationToMinutes } from './durationParse';

/** A length under this is a quick stop (Change time's stepper, a chained stop ended early). */
export const QUICK_STOP_UNDER = 15;
/** Quick stops starting within this of the one before share a row on the day grid. */
export const QUICK_RUN_GAP = 15;
const DEFAULT_DURATION = 60;
const DAY = 24 * 60;
const LATE_NIGHT_CUTOFF = 6 * 60;

const toMinutes = (time) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};
const startOf = (a) => toMinutes(a?.scheduledTime || a?.scheduled_time || a?.time);

/** Whether a stop is a quick stop: marked one, and at an exact time (an untimed stop has no minute to be at). */
export function isQuickStop(activity) {
  return !!(activity && (activity.quickStop || activity.quick_stop === 1 || activity.quick_stop === true) && startOf(activity) !== null);
}

/**
 * How many minutes a stop holds: 0 for a quick stop, else its own length, else
 * `fallback` (an hour — what a stop with no known length has always meant).
 */
export function stopLengthMinutes(activity, fallback = DEFAULT_DURATION) {
  if (isQuickStop(activity)) return 0;
  return parseDurationToMinutes(activity?.duration, 0) || fallback;
}

/**
 * Runs of quick stops on a day, for one row each: consecutive (by start)
 * quick stops, each starting within QUICK_RUN_GAP of the one before, two or
 * more of them. `entries` are anything with a start in minutes (`startOf` reads
 * it) and `isQuick` says which count. → [{ items, start, end }] in start order;
 * a lone quick stop is no run.
 */
export function quickStopRuns(entries, { startOf: start = (e) => e.start, isQuick = (e) => e.quick } = {}) {
  const quick = (entries || []).filter(e => e && isQuick(e) && Number.isFinite(start(e)))
    .slice().sort((x, y) => start(x) - start(y));
  const runs = [];
  let cur = null;
  quick.forEach((e) => {
    const s = start(e);
    if (cur && s - cur.end <= QUICK_RUN_GAP) {
      cur.items.push(e);
      cur.end = s;
    } else {
      cur = { items: [e], start: s, end: s };
      runs.push(cur);
    }
  });
  return runs.filter(r => r.items.length > 1);
}

// A stop's start in its day's frame: a late-night stop (evening block, before
// 06:00) is past 24:00 — utils/tripNow's frame, kept local to avoid a cycle.
function frameStart(a) {
  const clock = startOf(a);
  if (clock === null) return null;
  const block = a?.timeBlock || a?.time_block;
  return clock < LATE_NIGHT_CUTOFF && block === 'evening' ? clock + DAY : clock;
}

/**
 * The stop the next "I'm here" ends: the latest-starting stop on `day` you
 * made by "I'm here" (`addedVia: 'here'`) that is still running at `nowMinutes`
 * (in the day's frame, utils/tripNow) — started, not yet over. A quick stop
 * has nothing running. `exclude`: a stop to leave out (the place being added
 * again). → the activity, or null.
 */
export function runningHereStop(day, nowMinutes, { exclude = null } = {}) {
  if (!Number.isFinite(nowMinutes)) return null;
  let best = null;
  let bestStart = -1;
  (day?.activities || []).forEach((a) => {
    // Anyone's on the trip (Kurtis 2026-10-28, ADD_NOW_PLAN decision 18): you
    // walk together, so the next "I'm here" ends the stop running, whoever
    // started it — the server shares the mark with the trip's travellers.
    if (!a || a === exclude || (a.addedVia || a.added_via) !== 'here' || isQuickStop(a)) return;
    // A walk made by "I'm here" is open, not running (utils/dayWalks): the
    // next "I'm here" joins it and never ends it.
    if (a.ownWalk || (a.activityType || a.activity_type) === 'walk' || a.category === 'walk') return;
    const start = frameStart(a);
    if (start === null) return;
    const end = start + stopLengthMinutes(a);
    if (start <= nowMinutes && end > nowMinutes && start > bestStart) {
      best = a;
      bestStart = start;
    }
  });
  return best;
}

/**
 * The change that ends `stop` at `nowMinutes` (day frame): its length becomes
 * the minutes it ran, or it becomes a quick stop when that's under
 * QUICK_STOP_UNDER. → a patch for the planner ({ duration, quickStop }), or
 * null when it can't be ended (not started yet, untimed).
 */
export function endStopAt(stop, nowMinutes) {
  const start = frameStart(stop);
  if (start === null || !Number.isFinite(nowMinutes) || nowMinutes < start) return null;
  const ran = Math.round(nowMinutes - start);
  return ran < QUICK_STOP_UNDER
    ? { duration: null, durationHours: undefined, durationMinutes: undefined, quickStop: true }
    : { duration: ran, durationHours: undefined, durationMinutes: undefined, quickStop: false };
}

const keyOf = (a) => (a && (a.activityId || a.activity_id || a.clientActivityId)) || null;

/**
 * The days after an edit, with "I'm here" dropped from any stop given another
 * time (Kurtis 2026-10-25, phase 2's known gap): moved, it is a plan, and the
 * next "I'm here" must not end it. A walk keeps its own (its time is its
 * places' — utils/dayWalks). The op applier clears it the same way. Also no
 * untimed stop stays a quick stop (withUntimedNotQuick).
 * Returns `nextDays` itself when nothing changed (the funnel's no-op rule).
 */
export function withHereClearedOnRetime(prevDays, nextDays) {
  return withUntimedNotQuick(hereCleared(prevDays, nextDays));
}

/**
 * A stop with no time is no quick stop (it has no minute to be at): one made
 * untimed drops the marker, so timed again later it has a length (phase 2's
 * known gap). → `days` itself when none is.
 */
export function withUntimedNotQuick(days) {
  if (!Array.isArray(days)) return days;
  let changed = false;
  const out = days.map((day) => {
    const acts = day?.activities;
    if (!Array.isArray(acts) || !acts.some(a => a && a.quickStop && !(a.scheduledTime || a.scheduled_time))) return day;
    changed = true;
    return { ...day, activities: acts.map(a => (a && a.quickStop && !(a.scheduledTime || a.scheduled_time) ? { ...a, quickStop: false } : a)) };
  });
  return changed ? out : days;
}

function hereCleared(prevDays, nextDays) {
  if (!Array.isArray(nextDays)) return nextDays;
  const before = new Map();
  (prevDays || []).forEach(d => (d?.activities || []).forEach((a) => { const k = keyOf(a); if (k) before.set(k, a); }));
  let changed = false;
  const days = nextDays.map((day) => {
    const acts = day?.activities;
    if (!Array.isArray(acts)) return day;
    let dayChanged = false;
    const next = acts.map((a) => {
      if (!a || (a.addedVia || a.added_via) !== 'here' || a.ownWalk || (a.activityType || a.activity_type) === 'walk') return a;
      const was = before.get(keyOf(a));
      // Marked by this very edit (a planned stop started by "I'm here", phase 4): it stands.
      if (!was || (was.addedVia || was.added_via) !== 'here' || (was.scheduledTime || null) === (a.scheduledTime || null)) return a;
      dayChanged = true;
      return { ...a, addedVia: null };
    });
    if (!dayChanged) return day;
    changed = true;
    return { ...day, activities: next };
  });
  return changed ? days : nextDays;
}
